import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { SessionDataResult, MetricResult, CommentDataResult } from '../../../interfaces/services';
import { CustomMetric, CustomDetail, SessionMetric } from '../../../interfaces/db';

interface RequestState {
  isLoading: boolean;
  error: string | null;
}

export enum SessionRequestType {
  SUMMARY = 'summary',
  EMAIL = 'email',
  COACHING = 'coaching',
  CUSTOM_SCORE = 'custom_score'
}

interface SessionRequestStates {
  [SessionRequestType.SUMMARY]?: RequestState;
  [SessionRequestType.EMAIL]?: RequestState;
  [SessionRequestType.COACHING]?: RequestState;
  [SessionRequestType.CUSTOM_SCORE]?: RequestState;
}

export interface SessionDetailsState {
  sessionIdToDetails: {
    [sessionId: string]: {
      data: SessionDataResult | null;
      isLoading: boolean;
      error: string | null;
      requestStates: SessionRequestStates;
    }
  }
}

const initialRequestState: RequestState = {
  isLoading: false,
  error: null
};

const initialSessionRequestStates: SessionRequestStates = {
  [SessionRequestType.SUMMARY]: initialRequestState,
  [SessionRequestType.EMAIL]: initialRequestState,
  [SessionRequestType.COACHING]: initialRequestState,
  [SessionRequestType.CUSTOM_SCORE]: initialRequestState
};

const initialState: SessionDetailsState = {
  sessionIdToDetails: {}
};

const processData = (data: SessionDataResult): SessionDataResult => {
  const comments = data.comments ?? []
  const turns = (data.turns ?? []).sort((a, b) => a.start - b.start).filter((turn) => turn.text !== '')
  const updatedComments = comments.map((comment) => {
    if (!comment.start || !comment.end) return comment;
    const turn = turns.findIndex((turn) => comment.start && comment.end && turn.start && turn.end && Math.floor(turn.start) <= comment.start && Math.ceil(turn.end) >= comment.end && turn.text !== '');
    if (turn === -1) return comment;
    return { ...comment, transcriptIdx: turn };
  })
  return {
    ...data,
    comments: updatedComments,
    turns: turns
  }
}

export const sessionDetailsSlice = createSlice({
  name: 'sessionDetails',
  initialState,
  reducers: {
    startPendingComment: (state, action: PayloadAction<{sessionId: string, comment: CommentDataResult}>) => {
      const { sessionId, comment } = action.payload;
      const comments = state.sessionIdToDetails[sessionId].data?.comments ?? [];
      let idx = comments.findIndex((value) => (value.transcriptIdx ?? -1) > (comment.transcriptIdx ?? -1));
      if (idx === -1) idx = comments.length;
  
      const updatedComments = [
        ...comments.slice(0, idx),
        comment,
        ...comments.slice(idx)
      ];
      state.sessionIdToDetails[sessionId] = {
        ...state.sessionIdToDetails[sessionId],
        data: {
          ...state.sessionIdToDetails[sessionId].data!,
          comments: updatedComments
        },
      };
    },
    updateComment: (state, action: PayloadAction<{sessionId: string, commentId: string, partialComment: Partial<CommentDataResult>}>) => {
      const { sessionId, commentId, partialComment } = action.payload;
      state.sessionIdToDetails[sessionId] = {
        ...state.sessionIdToDetails[sessionId],
        data: {
          ...state.sessionIdToDetails[sessionId].data!,
          comments: state.sessionIdToDetails[sessionId].data!.comments!.map((comment) => comment.commentId === commentId ? { ...comment, ...partialComment } : comment)
        },
      };
    },
    removeCommentId: (state, action: PayloadAction<{sessionId: string, commentId: string}>) => {
      const { sessionId, commentId } = action.payload;
      state.sessionIdToDetails[sessionId] = {
        ...state.sessionIdToDetails[sessionId],
        data: {
          ...state.sessionIdToDetails[sessionId].data!,
          comments: state.sessionIdToDetails[sessionId].data!.comments!.filter((comment) => comment.commentId !== commentId)
        },
      };
    },
    startLoading: (state, action: PayloadAction<string>) => {
      const sessionId = action.payload;
      state.sessionIdToDetails[sessionId] = {
        ...state.sessionIdToDetails[sessionId],
        isLoading: true,
        error: null
      };
    },
    stopLoading: (state, action: PayloadAction<string>) => {
      const sessionId = action.payload;
      state.sessionIdToDetails[sessionId] = {
        ...state.sessionIdToDetails[sessionId],
        isLoading: false
      };
    },
    loadSuccess: (state, action: PayloadAction<{ sessionId: string; data: SessionDataResult }>) => {
      const { sessionId, data } = action.payload;
      state.sessionIdToDetails[sessionId] = {
        data: processData(data),
        isLoading: false,
        error: null,
        requestStates: state.sessionIdToDetails[sessionId]?.requestStates ?? initialSessionRequestStates
      };
    },
    loadError: (state, action: PayloadAction<{ sessionId: string; error: string }>) => {
      const { sessionId, error } = action.payload;
      state.sessionIdToDetails[sessionId] = {
        ...state.sessionIdToDetails[sessionId],
        isLoading: false,
        error
      };
    },
    startRequest: (state, action: PayloadAction<{ sessionId: string; requestType: SessionRequestType }>) => {
      const { sessionId, requestType } = action.payload;
      if (!state.sessionIdToDetails[sessionId]) {
        state.sessionIdToDetails[sessionId] = {
          data: null,
          isLoading: false,
          error: null,
          requestStates: initialSessionRequestStates
        };
      }
      state.sessionIdToDetails[sessionId].requestStates[requestType] = {
        isLoading: true,
        error: null
      };
    },
    stopRequest: (state, action: PayloadAction<{ sessionId: string; requestType: SessionRequestType }>) => {
      const { sessionId, requestType } = action.payload;
      if (state.sessionIdToDetails[sessionId]) {
        state.sessionIdToDetails[sessionId].requestStates[requestType] = {
          isLoading: false,
          error: null 
        };
      }
    },
    updateSessionMetric: (state, action: PayloadAction<{ 
      sessionId: string; 
      sessionMetric: Partial<SessionMetric>;
    }>) => {
      const { sessionId, sessionMetric } = action.payload;
      if (state.sessionIdToDetails[sessionId]) {
        state.sessionIdToDetails[sessionId].data = {  
          ...state.sessionIdToDetails[sessionId].data!,
          sessionMetric: {
            ...state.sessionIdToDetails[sessionId].data!.sessionMetric!,
            ...sessionMetric
          }
        };
      }
    },
    updateCustomMetric: (state, action: PayloadAction<{ 
      sessionId: string; 
      metric: CustomMetric;
      detail: CustomDetail;
    }>) => {
      const { sessionId, metric, detail } = action.payload;
      if (state.sessionIdToDetails[sessionId]) {
        state.sessionIdToDetails[sessionId].data = {  
          ...state.sessionIdToDetails[sessionId].data!,
          customDetail: detail,
          customMetric: metric,
        };
      }
    },
    requestError: (state, action: PayloadAction<{ 
      sessionId: string; 
      requestType: SessionRequestType;
      error: string;
    }>) => {
      const { sessionId, requestType, error } = action.payload;
      if (state.sessionIdToDetails[sessionId]) {
        state.sessionIdToDetails[sessionId].requestStates[requestType] = {
          isLoading: false,
          error
        };
      }
    },
    clearSessionData: (state, action: PayloadAction<string>) => {
      const sessionId = action.payload;
      delete state.sessionIdToDetails[sessionId];
    },
  }
})

export const { 
  startLoading, 
  stopLoading,
  loadSuccess, 
  loadError,
  startRequest,
  stopRequest,
  updateSessionMetric,
  updateCustomMetric,
  requestError,
  clearSessionData,
  startPendingComment,
  updateComment,
  removeCommentId,
} = sessionDetailsSlice.actions;

export default sessionDetailsSlice.reducer;
